import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

export default function SponsorAccount() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    })();
  }, []);

  if (!me) return <div className="text-sm text-slate-400">Loading…</div>;

  const update = (k, v) => setMe((m) => ({ ...m, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        name: me.name,
        phone: me.phone,
        company: me.company,
        bio: me.bio,
        years_experience: Number(me.years_experience || 0),
        total_deals: Number(me.total_deals || 0),
        specialties: Array.isArray(me.specialties) ? me.specialties : String(me.specialties || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.patch("/sponsor/profile", payload);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const specialtiesStr = Array.isArray(me.specialties) ? me.specialties.join(", ") : (me.specialties || "");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Account Settings</h1>
        <p className="mt-1 text-sm text-slate-400">Update your public broker profile.</p>
      </div>
      <form onSubmit={save} className="space-y-6" data-testid="account-form">
        <Card className="border-slate-800 bg-slate-900 p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Full Name"><Input value={me.name || ""} onChange={(e) => update("name", e.target.value)} className={INP} data-testid="acct-name" /></F>
            <F label="Phone"><Input value={me.phone || ""} onChange={(e) => update("phone", e.target.value)} className={INP} data-testid="acct-phone" /></F>
            <F label="Company" span={2}><Input value={me.company || ""} onChange={(e) => update("company", e.target.value)} className={INP} data-testid="acct-company" /></F>
            <F label="Years in RE"><Input type="number" value={me.years_experience || 0} onChange={(e) => update("years_experience", e.target.value)} className={INP} data-testid="acct-years" /></F>
            <F label="Total Deals"><Input type="number" value={me.total_deals || 0} onChange={(e) => update("total_deals", e.target.value)} className={INP} data-testid="acct-deals" /></F>
            <F label="Specialties (comma-separated)" span={2}>
              <Input value={specialtiesStr} onChange={(e) => update("specialties", e.target.value)} className={INP} data-testid="acct-specialties" />
            </F>
            <F label="Bio" span={2}>
              <Textarea value={me.bio || ""} onChange={(e) => update("bio", e.target.value)} className={`${INP} min-h-[120px]`} data-testid="acct-bio" />
            </F>
          </div>
        </Card>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60" data-testid="acct-save-btn">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const INP = "border-slate-800 bg-slate-950 text-slate-50 focus-visible:ring-emerald-500/40";
function F({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
